import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import PageHero from "../PageHero.jsx";
import DataTable from "../DataTable.jsx";
import ScrollArea from "../ScrollArea.jsx";
import ColumnPicker from "../ColumnPicker.jsx";
import OverflowTooltip from "../OverflowTooltip.jsx";
import SegmentedTabs from "../SegmentedTabs.jsx";
import ColumnHeaderRow from "../ColumnHeaderRow.jsx";
import MemoryXrayView from "./MemoryXrayView.jsx";
import { useDataTableColumns } from "../../lib/columnSelection.js";
import { findContentScrollRoot } from "../../lib/scrollRoot.js";
import { NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact } from "../../lib/renderers.js";
import { listAllMemoryFiles, listMemoryPeople, listOrgLessons } from "../../lib/memories.js";
import { dashboardScopeToApiScope } from "../../lib/analyticsClient.js";
import { useLabels, scopeStepLabel } from "../../lib/dimensionDefs.js";

// Memories DIMENSION lens — the store overview, rendered when the Memories dim
// is FOCUSED with no specific memory selected. Built on the shared DataTable so
// it reads like the Inventory page, with a Store / Lessons toggle:
//
//   • STORE (scope-aware, memory is per-person):
//       - scoped to a person → a table of every memory they track across all
//         their projects;
//       - at the tenant level → a roster of people who have memory; click one to
//         scope to them (carrying the memory focus) and land on their store.
//   • LESSONS (cross-person): the same memories grouped by lesson ACROSS people,
//       showing each one's spread (held by N people / M projects). Makes the
//       siloing of agent-learned lessons legible — the first give-it-back slice
//       (O-2345). Click a lesson → its fleet detail (who holds it, distinct
//       copies).
const TABS = [
  { id: "store", eyebrow: "Store", title: "By endpoint" },
  { id: "lessons", eyebrow: "Lessons", title: "Across endpoints" },
];

export default function MemoryStoreLens({ nav, ctx }) {
  const scopePath = nav?.scopePath || [];
  const range = ctx?.range;
  const scopeKey = useMemo(() => JSON.stringify(scopePath), [scopePath]);
  const personScoped = useMemo(
    () => scopePath.some((s) => s.dim === "endpoints" || s.dim === "identities"),
    [scopePath],
  );
  const [tab, setTab] = useState("store");
  const [xray, setXray] = useState(false);
  const [state, setState] = useState({ loading: true, files: [], people: [], lessons: [] });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, files: [], people: [], lessons: [] });
    // Translate the dashboard scope (plural dims) to API form, or the query
    // builds no filter and runs UNSCOPED across the whole tenant.
    const { apiScope } = dashboardScopeToApiScope(scopePath);
    let p;
    if (tab === "lessons") {
      p = listOrgLessons({ apiScope, range, limit: 1000 }).then((lessons) => ({ files: [], people: [], lessons }));
    } else if (personScoped) {
      p = listAllMemoryFiles({ apiScope, range, limit: 1000 }).then((files) => ({ files, people: [], lessons: [] }));
    } else {
      p = listMemoryPeople({ apiScope, range, limit: 200 }).then((people) => ({ files: [], people, lessons: [] }));
    }
    p.then((r) => { if (!cancelled) setState({ loading: false, ...r }); })
      .catch(() => { if (!cancelled) setState({ loading: false, files: [], people: [], lessons: [] }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, range, personScoped, tab]);

  // Resolve endpoint → person labels for the roster (no-op outside the people
  // view). labelTick must feed the memo or names stay stuck on the id fallback.
  const peopleSteps = useMemo(
    () => state.people.map((pp) => ({ dim: "endpoints", id: pp.endpointId })),
    [state.people],
  );
  const labelTick = useLabels(peopleSteps);
  const peopleRows = useMemo(
    () => state.people.map((pp) => ({ ...pp, id: pp.endpointId, name: scopeStepLabel({ dim: "endpoints", id: pp.endpointId }) })),
    [state.people, labelTick],
  );

  const selectTab = useCallback((t) => { setXray(false); setTab(t); }, []);
  const tabProps = { tabs: TABS, activeTab: tab, onSelectTab: selectTab };
  if (tab === "lessons") {
    if (xray) return <MemoryXrayView scopePath={scopePath} range={range} onBack={() => setXray(false)} onOpenDetail={ctx?.onOpenDetail} onNavigatePath={ctx?.onNavigatePath} />;
    return <LessonsTable rows={state.lessons} loading={state.loading} scopePath={scopePath} scopeKey={scopeKey} ctx={ctx} onXray={() => setXray(true)} {...tabProps} />;
  }
  return personScoped
    ? <FilesTable rows={state.files} loading={state.loading} scopePath={scopePath} scopeKey={scopeKey} ctx={ctx} {...tabProps} />
    : <PeopleTable rows={peopleRows} loading={state.loading} scopePath={scopePath} scopeKey={scopeKey} ctx={ctx} {...tabProps} />;
}

// ── shared bits ──────────────────────────────────────────────────────

function sortRows(rows, sortBy) {
  if (!sortBy) return rows;
  const { id, dir } = sortBy;
  const mul = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[id];
    const bv = b[id];
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * mul;
    return String(av ?? "").localeCompare(String(bv ?? "")) * mul;
  });
}

function Tag({ children }) {
  return (
    <span className="inline-block max-w-full truncate rounded-[3px] border border-[color:var(--line-strong)] px-[5px] py-[1px] text-[10px] leading-[1.3] text-[color:var(--muted)]">
      {children}
    </span>
  );
}

function Num({ value }) {
  return <span className="text-[11px] tabular-nums text-[color:var(--muted)]">{formatCompact(value)}</span>;
}

function useSort(initial) {
  const [sortBy, setSortBy] = useState(initial);
  const onSortChange = useCallback((id) => {
    setSortBy((cur) => (cur?.id === id ? { id, dir: cur.dir === "asc" ? "desc" : "asc" } : { id, dir: "desc" }));
  }, []);
  return [sortBy, onSortChange];
}

// Header shell shared by every view. The hero + tabs scroll away; the search +
// column picker + count strip + column header pin together in a STICKY toolbar
// against the page scroll (the scope-overview / trace-log pattern), so the rows
// scroll under them. DataTable runs showHeader={false} — its header lives here.
function StoreHeader({ hero, search, setSearch, placeholder, cols, sortBy, onSortChange, shownCount, isFiltering, loading, tabs, activeTab, onSelectTab, minWidth, action = null }) {
  return (
    <>
      <div className="grid gap-[10px]">
        <PageHero eyebrow="Memory" title="Memory store" detail={hero.detail} metrics={hero.metrics} />
        <SegmentedTabs tabs={tabs} active={activeTab} onSelect={onSelectTab} />
      </div>
      <div className="sticky top-0 z-20 grid gap-[8px] bg-[color:var(--bg)] pt-[6px] shadow-[0_1px_0_var(--line-soft)]">
        <div className="grid items-end gap-[8px] [grid-template-columns:minmax(0,1fr)_auto]">
          <label className="grid gap-[5px]">
            <span className={NOTE_CLASS}>Search</span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={placeholder}
              className="min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
            />
          </label>
          <div className="flex items-end gap-[8px]">
            {action}
            <ColumnPicker {...cols.pickerProps} variant="input" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] text-[11px] leading-[1.18] text-[color:var(--muted)]">
          <span>
            <strong className="text-[color:var(--ink)]">{formatCompact(shownCount)}</strong>{" "}
            {isFiltering ? "matching" : "shown"}
          </span>
          {loading ? <span className={NOTE_CLASS}>Loading…</span> : null}
        </div>
        <ColumnHeaderRow cols={cols} sortBy={sortBy} onSortChange={onSortChange} minWidth={minWidth} />
      </div>
    </>
  );
}

// ── STORE / person-scoped: the memory files table ────────────────────

const FILE_COLUMNS = [
  { id: "label", label: "Memory", flex: true },
  { id: "kind", label: "Type", width: 120 },
  { id: "projectLabel", label: "Project", width: 180 },
  { id: "value", label: "Traces", width: 90 },
];
const FILE_DEFAULT_VISIBLE = ["label", "kind", "projectLabel", "value"];

function renderFileCell(row, col) {
  if (col.id === "value") return <Num value={row.value} />;
  if (col.id === "kind") return <Tag>{row.kind}</Tag>;
  if (col.id === "projectLabel") return <Tag>{row.projectLabel}</Tag>;
  return <OverflowTooltip as="span" text={row.label} className="truncate text-[13px] leading-[1.15] text-[color:var(--ink)]" />;
}

function FilesTable({ rows, loading, scopePath, scopeKey, ctx, tabs, activeTab, onSelectTab }) {
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [sortBy, onSortChange] = useSort({ id: "value", dir: "desc" });
  const cols = useDataTableColumns({ storageKey: "memoryStoreFiles", columns: FILE_COLUMNS, defaultVisible: FILE_DEFAULT_VISIBLE });

  const q = deferredSearch.trim().toLowerCase();
  const isFiltering = q.length > 0;
  const visibleRows = useMemo(() => {
    const filtered = q
      ? rows.filter((r) => r.label.toLowerCase().includes(q) || r.projectLabel.toLowerCase().includes(q) || r.kind.toLowerCase().includes(q))
      : rows;
    return sortRows(filtered, sortBy);
  }, [rows, q, sortBy]);
  const projectCount = useMemo(() => new Set(rows.map((r) => r.projectDir)).size, [rows]);

  // The memory dim is FLAT (keyed on the bare slug — project→file hierarchy was
  // dropped), so a row opens a SINGLE memories step. The old two-step form
  // (memories:projectDir / memories:slug) prepended an empty `memories:` step once
  // projectDir went blank (the memory.project facet is forward-fill), which the
  // trace query read as `tag = ""` ANDed with the real tag → zero rows.
  const onRowClick = useCallback(
    (row) => ctx?.onNavigatePath?.([...scopePath, { dim: "memories", ids: [row.slug], id: row.slug }]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx?.onNavigatePath, scopeKey],
  );

  return (
    <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[10px] pb-[18px]">
      <StoreHeader
        hero={{
          detail: "Every memory this person's harness tracks, across all their projects. Click one to open its content and version history.",
          metrics: [
            { label: "Memories", value: loading ? "–" : formatCompact(rows.length) },
            { label: "Projects", value: loading ? "–" : formatCompact(projectCount) },
          ],
        }}
        search={search}
        setSearch={setSearch}
        placeholder="Memory name, project, type…"
        cols={cols}
        sortBy={sortBy}
        onSortChange={onSortChange}
        shownCount={visibleRows.length}
        isFiltering={isFiltering}
        loading={loading}
        tabs={tabs}
        activeTab={activeTab}
        onSelectTab={onSelectTab}
        minWidth={520}
      />
      <div className="min-w-0">
        <DataTable
          cols={cols}
          rows={visibleRows}
          getRowId={(r) => `${r.projectDir}\n${r.slug}`}
          renderCell={renderFileCell}
          onRowClick={onRowClick}
          sortBy={sortBy}
          onSortChange={onSortChange}
          loading={loading}
          skeletonRows={6}
          findScrollContainer={findContentScrollRoot}
          showHeader={false}
          minWidth={520}
          empty={
            <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px] my-[12px]">
              <strong className="text-[13px] leading-[1.08] font-medium">
                {isFiltering ? "No memories match this search" : "No memories in scope"}
              </strong>
              <span className={NOTE_CLASS}>
                {isFiltering ? "Clear the search to bring them back." : "Memories appear here once this person's harness writes them."}
              </span>
            </div>
          }
        />
      </div>
    </ScrollArea>
  );
}

// ── STORE / tenant level: the people roster table ────────────────────

const PEOPLE_COLUMNS = [
  { id: "name", label: "Endpoint", flex: true },
  { id: "value", label: "Memories", width: 120 },
  { id: "projectCount", label: "Projects", width: 110 },
];
const PEOPLE_DEFAULT_VISIBLE = ["name", "value", "projectCount"];

function renderPeopleCell(row, col) {
  if (col.id === "value") return <Num value={row.value} />;
  if (col.id === "projectCount") return <Num value={row.projectCount} />;
  return <OverflowTooltip as="span" text={row.name} className="truncate text-[13px] leading-[1.15] text-[color:var(--ink)]" />;
}

function PeopleTable({ rows, loading, scopePath, scopeKey, ctx, tabs, activeTab, onSelectTab }) {
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [sortBy, onSortChange] = useSort({ id: "value", dir: "desc" });
  const cols = useDataTableColumns({ storageKey: "memoryStorePeople", columns: PEOPLE_COLUMNS, defaultVisible: PEOPLE_DEFAULT_VISIBLE });

  const q = deferredSearch.trim().toLowerCase();
  const isFiltering = q.length > 0;
  const visibleRows = useMemo(() => {
    const filtered = q ? rows.filter((r) => r.name.toLowerCase().includes(q)) : rows;
    return sortRows(filtered, sortBy);
  }, [rows, q, sortBy]);
  const totalMemories = useMemo(() => rows.reduce((n, r) => n + (r.value || 0), 0), [rows]);

  // Carry the memory focus into the person's scope, so the click lands on THEIR
  // store (the files table) rather than their generic endpoint overview.
  const onRowClick = useCallback(
    (row) => ctx?.onNavigatePath?.(
      [...scopePath, { dim: "endpoints", ids: [row.endpointId], id: row.endpointId }],
      { focus: "memories" },
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx?.onNavigatePath, scopeKey],
  );

  return (
    <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[10px] pb-[18px]">
      <StoreHeader
        hero={{
          detail: "Each endpoint keeps its own memory store. Select an endpoint to open it.",
          metrics: [
            { label: "Endpoints", value: loading ? "–" : formatCompact(rows.length) },
            { label: "Memories", value: loading ? "–" : formatCompact(totalMemories) },
          ],
        }}
        search={search}
        setSearch={setSearch}
        placeholder="Endpoint…"
        cols={cols}
        sortBy={sortBy}
        onSortChange={onSortChange}
        shownCount={visibleRows.length}
        isFiltering={isFiltering}
        loading={loading}
        tabs={tabs}
        activeTab={activeTab}
        onSelectTab={onSelectTab}
        minWidth={420}
      />
      <div className="min-w-0">
        <DataTable
          cols={cols}
          rows={visibleRows}
          getRowId={(r) => r.endpointId}
          renderCell={renderPeopleCell}
          onRowClick={onRowClick}
          sortBy={sortBy}
          onSortChange={onSortChange}
          loading={loading}
          skeletonRows={6}
          findScrollContainer={findContentScrollRoot}
          showHeader={false}
          minWidth={420}
          empty={
            <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px] my-[12px]">
              <strong className="text-[13px] leading-[1.08] font-medium">
                {isFiltering ? "No people match this search" : "No memory in scope"}
              </strong>
              <span className={NOTE_CLASS}>
                {isFiltering ? "Clear the search to bring them back." : "People appear here once their harness writes memory."}
              </span>
            </div>
          }
        />
      </div>
    </ScrollArea>
  );
}

// ── LESSONS: cross-person, grouped by lesson ─────────────────────────

const LESSON_COLUMNS = [
  { id: "label", label: "Lesson", flex: true },
  { id: "kind", label: "Type", width: 120 },
  { id: "endpointCount", label: "Endpoints", width: 110 },
  { id: "projectCount", label: "Projects", width: 100 },
  { id: "value", label: "Activity", width: 100 },
];
const LESSON_DEFAULT_VISIBLE = ["label", "kind", "endpointCount", "projectCount", "value"];

function renderLessonCell(row, col) {
  if (col.id === "value") return <Num value={row.value} />;
  if (col.id === "endpointCount") return <Num value={row.endpointCount} />;
  if (col.id === "projectCount") return <Num value={row.projectCount} />;
  if (col.id === "kind") return <Tag>{row.kind}</Tag>;
  return <OverflowTooltip as="span" text={row.label} className="truncate text-[13px] leading-[1.15] text-[color:var(--ink)]" />;
}

function LessonsTable({ rows, loading, scopePath, scopeKey, ctx, tabs, activeTab, onSelectTab, onXray }) {
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  // Default sort: most active first. "Endpoints" asc surfaces siloed-but-active.
  const [sortBy, onSortChange] = useSort({ id: "value", dir: "desc" });
  const cols = useDataTableColumns({ storageKey: "memoryOrgLessons", columns: LESSON_COLUMNS, defaultVisible: LESSON_DEFAULT_VISIBLE });

  const q = deferredSearch.trim().toLowerCase();
  const isFiltering = q.length > 0;
  const visibleRows = useMemo(() => {
    const filtered = q ? rows.filter((r) => r.label.toLowerCase().includes(q) || r.kind.toLowerCase().includes(q)) : rows;
    return sortRows(filtered, sortBy);
  }, [rows, q, sortBy]);
  const siloed = useMemo(() => rows.filter((r) => (r.endpointCount || 0) <= 1).length, [rows]);

  // Click a lesson → its cross-person detail (who holds it + distinct copies):
  // the existing per-memory value lens in byOwner mode. The BARE slug groups the
  // lesson across endpoints — the org lens, which is exactly what we want here.
  const onRowClick = useCallback(
    (row) => ctx?.onNavigatePath?.([...scopePath, { dim: "memories", ids: [row.slug], id: row.slug }]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx?.onNavigatePath, scopeKey],
  );

  return (
    <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[10px] pb-[18px]">
      <StoreHeader
        hero={{
          detail: "Lessons learned by the organization's agents, grouped across endpoints. A frequently used lesson held by one endpoint may be worth sharing. Select a lesson to see where it is stored.",
          metrics: [
            { label: "Lessons", value: loading ? "–" : formatCompact(rows.length) },
            { label: "Siloed", value: loading ? "–" : formatCompact(siloed), note: siloed ? "on one endpoint" : "" },
          ],
        }}
        search={search}
        setSearch={setSearch}
        placeholder="Lesson, type…"
        cols={cols}
        sortBy={sortBy}
        onSortChange={onSortChange}
        shownCount={visibleRows.length}
        isFiltering={isFiltering}
        loading={loading}
        tabs={tabs}
        activeTab={activeTab}
        onSelectTab={onSelectTab}
        minWidth={520}
        action={
          <button
            type="button"
            onClick={onXray}
            className="flex min-h-[30px] shrink-0 items-center gap-[5px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[12px] leading-[1.18] text-[color:var(--muted)] transition-colors hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
          >
            <span aria-hidden className="text-[color:var(--green)]">✦</span>
            Distill
          </button>
        }
      />
      <div className="min-w-0">
        <DataTable
          cols={cols}
          rows={visibleRows}
          getRowId={(r) => r.slug}
          renderCell={renderLessonCell}
          onRowClick={onRowClick}
          sortBy={sortBy}
          onSortChange={onSortChange}
          loading={loading}
          skeletonRows={6}
          findScrollContainer={findContentScrollRoot}
          showHeader={false}
          minWidth={520}
          empty={
            <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px] my-[12px]">
              <strong className="text-[13px] leading-[1.08] font-medium">
                {isFiltering ? "No lessons match this search" : "No lessons in scope"}
              </strong>
              <span className={NOTE_CLASS}>
                {isFiltering ? "Clear the search to bring them back." : "Lessons appear here once the org's harnesses write memory."}
              </span>
            </div>
          }
        />
      </div>
    </ScrollArea>
  );
}
